import DefaultText from 'components/common/DefaultText';
import ScreenLayout from 'components/common/ScreenLayout';
import React, {useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {userInfoState} from 'utils/Atoms';
import {minmax} from 'utils/minmax';
import {Line} from 'react-chartjs-2';
import {
  Chart,
  CategoryScale, // category scale 추가
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
} from 'chart.js';
import LabelPeriod from 'components/common/LabelPeriod';
import {searchActiveUserPeriod} from 'function/api';
import {IUsageData, IUsageDateType} from 'interfaces/interface';
import moment from 'moment-timezone';
import ContainerHeader from 'components/common/ContainerHeader';
import DefaultButton from 'components/common/DefaultButton';
import {excelDownloadUsage} from 'function/converter';
import LabelSelectYearMonth from 'components/common/LabelSelectYearMonth';
import {usageDateTypeList} from 'data/data';

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

/**
 * 날짜 타입에 따른 초기 선택 날짜 범위를 반환합니다.
 * @param {IUsageDateType} type - 날짜 타입 (yearly, monthly, daily)
 * @returns {Object} 시작일과 종료일을 포함한 객체
 * @returns {string} returns.start - 시작일 (ISO 문자열)
 * @returns {string} returns.end - 종료일 (ISO 문자열)
 */
const initSelectDate = (type: IUsageDateType) => {
  const today = new Date();
  const prevDate = new Date(today);

  if (type === 'yearly') {
    prevDate.setFullYear(today.getFullYear() - 5).toString();
  } else if (type === 'monthly') {
    prevDate.setMonth(today.getMonth() - 12).toString();
  } else {
    prevDate.setMonth(today.getMonth() - 1).toString();
  }

  return {
    start: prevDate.toISOString(),
    end: today.toISOString(),
  };
};

/**
 * 사용량 통계 페이지 컴포넌트
 * 활성 사용자 수를 기간별로 조회하고 차트로 표시합니다.
 * @returns {JSX.Element} 사용량 통계 페이지 컴포넌트
 */
const Usage = () => {
  // locale
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});

  const userInfo = useRecoilValue(userInfoState);

  const [dateType, setDateType] = useState<IUsageDateType>('yearly');
  const [selectDate, setSelectDate] = useState(initSelectDate('yearly'));

  const [usageData, setUsageData] = useState<null | IUsageData>(null);

  /**
   * 사용량 데이터를 조회합니다.
   * @param {Object} params - 조회 파라미터
   * @param {string} params.start - 시작일
   * @param {string} params.end - 종료일
   * @param {IUsageDateType} [params.currentDateType] - 현재 날짜 타입 (선택사항)
   * @returns {Promise<void>}
   */
  const getUsageData = async ({
    start,
    end,
    currentDateType,
  }: {
    start: string;
    end: string;
    currentDateType?: IUsageDateType;
  }) => {
    const result = await searchActiveUserPeriod({
      userInfo,
      setErrorModal,
      setIsLoading,
      startDate: moment(start).format('YYYY-MM-DD'),
      endDate: moment(end).format('YYYY-MM-DD'),
      type: currentDateType || dateType,
    });

    result && setUsageData(result);
  };

  useEffect(() => {
    getUsageData(selectDate);
  }, []);

  const labels = usageData
    ? usageData?.activeUsersList.map(item => item.date)
    : [];

  const usageDataLength = usageData?.activeUsersList?.length || 0;

  /**
   * 차트의 포인트 반지름 배열을 생성합니다.
   * 마지막 데이터 포인트에만 반지름을 설정합니다.
   * @returns {number[]} 포인트 반지름 배열
   */
  const createPointRadiusArray = () => {
    const arr = new Array(usageDataLength).fill(0); // 배열을 0으로 초기화
    arr[usageDataLength - 1] = 3; // 마지막 요소에 값 설정
    return arr;
  };

  const options = {
    responsive: true, // 반응형
    // maintainAspectRatio: false, // 비율 유지 안 함
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    // label: {},
    plugins: {
      legend: {
        // 범례
        display: false,
      },
      tooltip: {
        // 그래프 위 값 표시
        enabled: true,
        callbacks: {
          title: function (tooltipItems: any) {
            const item = tooltipItems[0]; // 첫 번째 데이터 포인트 기준
            return dateType === 'daily'
              ? item.label
              : moment(item.label).format(
                  dateType === 'monthly' ? 'YY-MM' : 'YYYY',
                ); // X축 레이블 반환
          },
          label: function (tooltipItem: any) {
            // Y축 값 표시 (툴팁의 본문)
            return `사용자 수: ${tooltipItem.raw}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false, // 자동 건너뛰기 비활성화
          // stepSize: 2, // X축 단위
          callback: function (value: any, index: number, values: any) {
            if (index === 0 || index === values.length - 1) {
              return dateType === 'daily'
                ? labels[index]
                : moment(labels[index]).format(
                    dateType === 'monthly' ? 'YY-MM' : 'YYYY',
                  ); // 첫 번째와 마지막 레이블은 그대로 표시
            }
            return '';
          },
          color: colors.primary2,
          font: {
            size: 16,
          },
        },
        border: {
          color: colors.primary2, // x축 단위 선 색깔 설정
          width: 2, // 선의 두께
        },
      },
      y: {
        grid: {
          color: '#E3E3E3',
        },
        ticks: {
          stepSize: 10,
          callback: (value: any) => value,
          color: colors.primary2,
          font: {
            size: 16,
          },
        },
        border: {
          width: 0,
        },
      },
    },
  };

  const data: ChartData<'line', number[], string> = {
    labels,
    datasets: [
      {
        type: 'line',
        label: '',
        backgroundColor: colors.primary2,
        borderColor: colors.primary2,
        data: usageData
          ? usageData?.activeUsersList.map(item => item.activeUsers)
          : [],
        pointRadius: createPointRadiusArray(), // 마지막 점에만 크기를 설정
        borderWidth: 3,
        // pointRadius: 0, // 라인의 점 없애기
        // borderDash: [5, 5], // 점선 설정
      },
    ],
  };

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      <Container>
        <ContainerHeader
          mainTitle="usage"
          mainTitleStyle={{fontSize: minmax(26), fontWeight: '500'}}
        />

        <Inner>
          <SwitchModeWrap>
            {usageDateTypeList.map(item => (
              <DefaultText
                key={item}
                text={item}
                textStyle={{
                  fontSize: minmax(25),
                  fontWeight: 'bold',
                  borderBottom: `3px solid ${
                    dateType === item ? colors.primary : 'transparent'
                  }`,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setDateType(item);
                  setSelectDate(initSelectDate(item));
                  getUsageData({
                    ...initSelectDate(item),
                    currentDateType: item,
                  });
                }}
              />
            ))}
          </SwitchModeWrap>

          {/* 날짜 선택 */}
          <SelectDateWrap>
            {dateType === 'daily' ? (
              <LabelPeriod
                labelText="date"
                labelStyle={{width: '10%'}}
                value={selectDate}
                setValue={setSelectDate}
                eachLimit={{endMaxDate: new Date()}}
              />
            ) : (
              <>
                <LabelSelectYearMonth
                  label="날짜"
                  date={{
                    year: new Date(selectDate.start).getFullYear(),
                    month: new Date(selectDate.start).getMonth() + 1,
                  }}
                  setDate={(date: {year: number; month: number}) =>
                    setSelectDate({
                      ...selectDate,
                      start: new Date(
                        date.year,
                        dateType === 'yearly' ? 0 : date.month - 1,
                        1,
                      ).toString(),
                    })
                  }
                  type={dateType === 'yearly' ? 'year' : 'both'}
                />
                <DefaultText text="~" textStyle={{fontSize: minmax(30)}} />
                <LabelSelectYearMonth
                  date={{
                    year: new Date(selectDate.end).getFullYear(),
                    month: new Date(selectDate.end).getMonth() + 1,
                  }}
                  setDate={(date: {year: number; month: number}) =>
                    setSelectDate({
                      ...selectDate,
                      end: new Date(
                        date.year,
                        dateType === 'yearly'
                          ? new Date().getFullYear() === date.year
                            ? new Date().getMonth()
                            : 11
                          : date.month - 1,
                        1,
                      ).toString(),
                    })
                  }
                  type={dateType === 'yearly' ? 'year' : 'both'}
                />
              </>
            )}
            <DefaultButton
              text="toSearch"
              actived={1}
              buttonStyle={{width: minmax(100)}}
              onClick={() => {
                getUsageData(selectDate);
              }}
            />
          </SelectDateWrap>

          <Line
            options={options}
            data={data}
            //   className="chart"
            // width="100%"
            // height="500px"
          />

          <DefaultButton
            text="saveExcel"
            actived={1}
            buttonStyle={{
              backgroundColor: colors.darkLimeGreen,
              alignSelf: 'flex-end',
              marginTop: minmax(25),
            }}
            onClick={() => {
              if (usageData && usageData.activeUsersList.length > 0)
                excelDownloadUsage({
                  data: usageData?.activeUsersList,
                  dateType: dateType,
                });
            }}
          />
        </Inner>
      </Container>
    </ScreenLayout>
  );
};

export default Usage;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  margin-bottom: ${minmax(50)};
`;

const Inner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${minmax(20)};
`;

const SwitchModeWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${minmax(20)};
  margin-bottom: ${minmax(30)};
`;

const SelectDateWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${minmax(10)};
  margin-bottom: ${minmax(30)};
`;
